import DOMPurify from 'dompurify'

/**
 * THE security boundary for inbox message bodies.
 *
 * Inbox bodies are fully attacker-controlled: any authenticated user can send
 * arbitrary content to their advisor, and NOTHING in the pipeline sanitizes it
 * (the BFF proxies verbatim; the API only length-checks). So every `v-html` of a
 * message body MUST go through this function — rendering a stored body raw is
 * stored XSS.
 *
 * Deliberately sanitize AT RENDER rather than only on write: every message sent
 * before the rich editor existed was stored unsanitized, so a write-side-only
 * fix would leave that whole backlog exploitable. Render-side covers old + new.
 *
 * The allowlist is intentionally narrow — it matches exactly what the Tiptap
 * composer can emit. Anything else (script/style/iframe/object/embed/form, all
 * `on*` handlers, `javascript:` and `data:` URLs) is dropped.
 *
 * Safe to call on the client only — the inbox is CSR (`'/**': { ssr: false }`),
 * so DOMPurify always has a real DOM here.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'hr',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'ul', 'ol', 'li',
  'blockquote', 'code', 'pre',
  'h1', 'h2', 'h3',
  'a'
]

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel']

// Only these URL schemes may appear in an href. Blocks `javascript:` and
// `data:` (the two that turn a link into script execution).
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|#|\/)/i

let hookInstalled = false

/** Force every surviving link to be safe to click from our origin. */
function installHooks() {
  if (hookInstalled || typeof window === 'undefined') return
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      // noopener/noreferrer: stop the opened tab from reaching back via
      // window.opener. nofollow: don't lend SEO weight to user-submitted links.
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer nofollow')
    }
  })
  hookInstalled = true
}

/**
 * Sanitize a rich-text message body for `v-html`.
 * Returns '' on the server (no DOM) so nothing unsafe can ever be emitted.
 */
export function sanitizeMessageHtml(html: string | null | undefined): string {
  if (!html) return ''
  if (typeof window === 'undefined') return ''
  installHooks()
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    // Drop the element AND its contents for these — otherwise a stripped
    // <script> would leak its source text into the rendered output.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_CONTENTS: ['script', 'style'],
    KEEP_CONTENT: true
    // DO NOT add `USE_PROFILES` here. DOMPurify treats it as a REPLACEMENT for
    // the allowlist, not an addition: purify.js does
    //     if (USE_PROFILES) { ALLOWED_TAGS = addToSet({}, text); ALLOWED_ATTR = create(null); ... }
    // which discards everything passed above and swaps in the full ~120-tag HTML
    // profile (img, video, style/class/id attributes, …). It shipped that way
    // briefly and made this entire allowlist inert.
  })
}

/**
 * Flatten a body to plain text for LIST PREVIEWS (inbox list, notification
 * popover). Previews are one-liners — rendering markup there would break the
 * truncation and leak layout. Works for both formats: strips tags from HTML
 * bodies, and strips leftover markdown punctuation from legacy bodies so old
 * messages don't preview as `**bold**`.
 */
export function messageBodyToPlainText(
  body: string | null | undefined,
  bodyFormat?: string | null
): string {
  if (!body) return ''
  let text = body
  if ((bodyFormat || '').toLowerCase() === 'html') {
    // Parse-then-read rather than regex-strip: a regex can be walked past with
    // malformed markup. textContent never executes anything.
    if (typeof window !== 'undefined') {
      const doc = new DOMParser().parseFromString(body, 'text/html')
      text = doc.body.textContent || ''
    } else {
      text = body.replace(/<[^>]*>/g, ' ')
    }
  } else {
    // Legacy markdown-ish source: drop the emphasis/heading punctuation.
    text = text.replace(/[*_`~]/g, '').replace(/^#{1,6}\s+/gm, '')
  }
  return text.replace(/\s+/g, ' ').trim()
}
